import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import { BackendType } from '@/models/general';
import {
    convertCouponFromResponse,
    convertCouponListItemFromResponse,
    Coupon,
    CouponListItem,
    CouponPage,
    CreateCoupon,
} from '@/models/coupon';

import { prepareForRequest } from '@/utils/api';

const baseUrl = urlBuilder('order', 'coupons');

type GetCouponsResponse = {
    coupons: CouponListItem<BackendType>[];
    total: number;
    page: number;
    size: number;
};

export type CouponListParams = {
    page: number;
    size: number;
    query?: string;
    /** Unchecked backoffice checkbox: active=true & expired=false. */
    active?: boolean;
    expired?: boolean;
    sort?: string;
    direction?: 'asc' | 'desc';
};

type UpdateCouponBody = {
    validFrom?: Date | string | null;
    validUntil: Date | string;
    amount: { amount: number; currency: string };
    contingent?: number;
    newCodes?: string;
};

export const couponApi = baseApiAuthenticated
    .enhanceEndpoints({ addTagTypes: ['COUPONS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getCoupons: builder.query<CouponPage, CouponListParams>({
                query: (params) => {
                    const search = new URLSearchParams();
                    search.set('page', String(params.page));
                    search.set('size', String(params.size));
                    if (params.query) search.set('query', params.query);
                    if (params.active !== undefined)
                        search.set('active', String(params.active));
                    if (params.expired !== undefined)
                        search.set('expired', String(params.expired));
                    if (params.sort) search.set('sort', params.sort);
                    if (params.direction)
                        search.set('direction', params.direction.toUpperCase());
                    return `${baseUrl}?${search.toString()}`;
                },

                transformResponse(response: GetCouponsResponse) {
                    return {
                        ...response,
                        coupons: response.coupons.map(
                            convertCouponListItemFromResponse
                        ),
                    };
                },

                providesTags: ['COUPONS'],
            }),

            getCouponDetail: builder.query<Coupon, string>({
                query: (id) => `${baseUrl}/${id}`,
                transformResponse(response: Coupon<BackendType>) {
                    return convertCouponFromResponse(response);
                },
                providesTags: (_result, _error, id) => [
                    { type: 'COUPONS', id },
                ],
            }),

            postCoupons: builder.mutation<Coupon, CreateCoupon>({
                query: (coupon) => {
                    const couponCopy = { ...coupon };

                    for (const key in couponCopy) {
                        if (couponCopy[key] === null) {
                            delete couponCopy[key];
                        }
                    }

                    return {
                        url: baseUrl,
                        method: 'POST',
                        body: prepareForRequest(couponCopy),
                    };
                },
                invalidatesTags: ['COUPONS'],
            }),

            putCoupon: builder.mutation<
                Coupon,
                { id: string; body: UpdateCouponBody }
            >({
                query: ({ id, body }) => ({
                    url: `${baseUrl}/${id}`,
                    method: 'PUT',
                    body: prepareForRequest(body),
                }),
                invalidatesTags: ['COUPONS'],
            }),

            patchCouponActive: builder.mutation<
                Coupon,
                { id: string; active: boolean }
            >({
                query: ({ id, active }) => ({
                    url: `${baseUrl}/${id}/active`,
                    method: 'PATCH',
                    body: { active },
                }),
                invalidatesTags: ['COUPONS'],
            }),
        }),
    });

export const {
    useGetCouponsQuery,
    useGetCouponDetailQuery,
    usePostCouponsMutation,
    usePutCouponMutation,
    usePatchCouponActiveMutation,
} = couponApi;
